import { useState, type CSSProperties } from "react";
import { useTranslation } from "react-i18next";
import { useProducts, usePatchProduct, useCreateProduct, type Product } from "@/api/admin/products";
import { AdminErrorState } from "@/components/admin/AdminErrorState";
import { InlineEditCell } from "@/components/admin/InlineEditCell";

const PAGE: CSSProperties = { padding: "16px 24px 96px", display: "flex", flexDirection: "column", gap: 16 };
const HEAD: CSSProperties = { display: "flex", justifyContent: "space-between", alignItems: "center" };
const H1: CSSProperties = { fontFamily: "var(--font-display)", fontSize: 22 };
const TABLE: CSSProperties = { width: "100%", borderCollapse: "collapse", fontFamily: "var(--font-mono)", fontSize: 13 };
const TH: CSSProperties = { textAlign: "left", padding: "8px 6px", borderBottom: "1px solid var(--d-line)",
                            color: "var(--d-fg-muted)", fontFamily: "var(--font-body)" };
const TD: CSSProperties = { padding: "8px 6px", borderBottom: "1px solid var(--d-line)" };
const BTN: CSSProperties = { padding: "8px 16px", borderRadius: 999, border: "1px solid var(--d-violet)",
                             background: "var(--d-violet)", color: "#fff", cursor: "pointer" };
const STATUS: CSSProperties = { fontSize: 12, color: "var(--d-fg-muted)" };
const HINT: CSSProperties = { fontSize: 11, fontWeight: 400, color: "var(--d-fg-muted)",
                              maxWidth: 220, whiteSpace: "normal" };

export function ProductsPage(): JSX.Element {
  const { t } = useTranslation("admin");
  const { data, isLoading, isError, refetch } = useProducts();
  const patch = usePatchProduct();
  const create = useCreateProduct();
  const [status, setStatus] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  function savePatch(code: string, fragment: Partial<Product>): void {
    patch.mutate(
      { code, patch: fragment },
      {
        onSuccess: () => setStatus(`${code}: ${Object.keys(fragment).join(", ")} ✓`),
        onError: (err) => setStatus(`${code}: ${(err as Error).message}`),
      },
    );
  }

  if (isError) return <AdminErrorState onRetry={() => void refetch()} />;

  return (
    <div style={PAGE}>
      <div style={HEAD}>
        <h2 style={H1}>{t("products.title")}</h2>
        <button style={BTN} onClick={() => setCreating(true)}>{t("products.create")}</button>
      </div>
      {status && <div role="status" style={STATUS}>{status}</div>}
      {isLoading && <div>{t("common.loading")}</div>}
      <table style={TABLE}>
        <thead>
          <tr>
            {(["code", "price", "grants", "active", "order"] as const).map((col) => (
              <th key={col} style={TH}>
                <div>{t(`products.col.${col}`)}</div>
                {/* The hint sits in the header, not per row: every cell in a
                    column is the same dial with the same range. */}
                <div style={HINT}>{t(`products.hint.${col}`)}</div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {(data ?? []).map((p) => (
            <tr key={p.code}>
              <td style={TD}>{p.code}</td>
              <td style={TD}>
                <InlineEditCell value={p.stars_price} type="number"
                                onSave={(v) => savePatch(p.code, { stars_price: Number(v) })} />
                {" "}★
              </td>
              <td style={TD}>{JSON.stringify(p.grants)}</td>
              <td style={TD}>
                <label>
                  <input type="checkbox"
                         aria-label={`${p.code} active`}
                         checked={p.is_active}
                         onChange={() => savePatch(p.code, { is_active: !p.is_active })} />
                </label>
              </td>
              <td style={TD}>
                <InlineEditCell value={p.display_order} type="number"
                                onSave={(v) => savePatch(p.code, { display_order: Number(v) })} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {creating && (
        <CreateProductForm
          submitting={create.isPending}
          onClose={() => setCreating(false)}
          onSubmit={(body) => create.mutate(body, {
            onSuccess: () => { setStatus(`${body.code} created ✓`); setCreating(false); },
            onError: (err) => setStatus((err as Error).message),
          })}
        />
      )}
    </div>
  );
}

function CreateProductForm(props: {
  onClose: () => void;
  onSubmit: (body: Omit<Product, "updated_at">) => void;
  submitting: boolean;
}): JSX.Element {
  const { t } = useTranslation("admin");
  const [code, setCode] = useState("");
  const [stars, setStars] = useState("0");
  const [order, setOrder] = useState("100");
  const [grantsText, setGrantsText] = useState('{"synastry": 1}');
  const [error, setError] = useState<string | null>(null);

  function submit() {
    let grants: Record<string, number> = {};
    try { grants = JSON.parse(grantsText); } catch { setError("Bad grants JSON"); return; }
    if (!code) { setError("Code required"); return; }
    if (Number(stars) < 0) { setError("stars_price must be ≥ 0"); return; }
    props.onSubmit({
      code, stars_price: Number(stars), grants,
      is_active: true, display_order: Number(order),
      title_ru: null, title_en: null, title_es: null,
      description_ru: null, description_en: null, description_es: null,
    });
  }

  const OVERLAY: CSSProperties = { position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)",
                                   display: "flex", alignItems: "center", justifyContent: "center", zIndex: 50 };
  const SHEET: CSSProperties = { background: "var(--d-ink-2)", borderRadius: 22, padding: 24,
                                 width: "calc(100% - 48px)", maxWidth: 480,
                                 display: "flex", flexDirection: "column", gap: 12 };
  const FIELD: CSSProperties = { padding: "8px 12px", borderRadius: 12, border: "1px solid var(--d-line)",
                                 background: "var(--d-ink)", color: "var(--d-fg)", fontFamily: "var(--font-mono)" };
  return (
    <div style={OVERLAY} onClick={props.onClose}>
      <div style={SHEET} onClick={(e) => e.stopPropagation()}>
        <h3 style={{ fontFamily: "var(--font-display)" }}>{t("products.create")}</h3>
        <input style={FIELD} placeholder="code" value={code} onChange={(e) => setCode(e.target.value)} />
        <input style={FIELD} type="number" placeholder="stars_price" value={stars} onChange={(e) => setStars(e.target.value)} />
        <input style={FIELD} type="number" placeholder="display_order" value={order} onChange={(e) => setOrder(e.target.value)} />
        <textarea style={{ ...FIELD, minHeight: 80 }} value={grantsText} onChange={(e) => setGrantsText(e.target.value)} />
        {error && <div style={{ color: "var(--d-ember)", fontSize: 12 }}>{error}</div>}
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <button style={{ padding: "8px 16px", borderRadius: 999, background: "transparent",
                           border: "1px solid var(--d-line)", color: "var(--d-fg)" }} onClick={props.onClose}>
            {t("common.cancel")}
          </button>
          <button disabled={props.submitting}
                  style={{ padding: "8px 16px", borderRadius: 999, background: "var(--d-violet)",
                           border: "none", color: "#fff" }} onClick={submit}>
            {t("common.save")}
          </button>
        </div>
      </div>
    </div>
  );
}
